// The full turn tracker (slice 12; design/16 §3, §5, §6, §7; design/20
// §3): the DM's tool, vertical by nature — one encounter with the strip,
// never a second list. Combatants added by hand (a name, a bonus, a count
// numbered by the rule tokens use), initiative rolled on the server as a
// system's initiative dice plus the bonus (issue 1303) or typed as a number, sorted, reordered by hand with
// the pointer following, linked to the tokens selected on the board (one
// action; a token another combatant held moves and the DM is told),
// effects as a label and a duration in one of the system's units of time
// (indefinite without one; issue 1303),
// the DM's priority list for the stones, and the encounter's controls. A
// player's panel is a read of their own strip, with End my turn.
// A system with dice pools (issue 1524; docs/MRCS/shadowrun-3.md §5.2): each row says its pools, *Combat 6/8 · Karma 3*,
// the DM types a size or what is left, and Refresh fills the pools the DM refreshes; a player sees their own alone.
// A system with initiative passes (issue 1525; §5.3): the phases of the round by pass, End phase, the round in the
// system's word ("Combat Turn 2 · Pass 3"); each row has the number of initiative dice, the change to the score since the
// roll (a wound's, typed by the DM until step 12 sets it) and the throw's working, "Init: 5 + 2d6 (2, 6) = 13".
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  LINK_HIDES, LINK_SHOWS, linkOffers, playerMoveNote, stripViewers, type LinkOffer, type LinkOffers,
  EFFECT_MOD_LIMITS, ENCOUNTER_LIMITS, combatantOf, combatantSeen, dispositionOf, effectBadge, emptyEncounter,
  type Disposition,
  endTurn, type Combatant, type Effect, type Encounter, type PlayerEncounter, type TableObject,
  POOL_LIMITS, poolWord, poolsLine, dmEncounterView, initiativeLine, waitingFor,
  floorOf,
  modTargetWords,
} from '@dragons-table/vtt-shared';
import { PhaseList, roundWords } from './PhaseList';
import { selection } from '../../board/selection';
import { boardBus } from '../../board/bus';
import { fightLeftovers } from '../../board/ClearFight';
import { useTableState } from '../../table/TableState';
import { useMayAct, useViewOnlyFor } from '../../table/mayAct';
import { placeKinds } from '../../place';
import { VIEW_ONLY_REASON } from '../../table/viewOnly';
import { useInitDraft, useStartEncounter } from './startEncounter';
import { useShell } from '../context';
import { CombatantEntry } from './EntryParts';
import { PlaceDialog } from './PlaceDialog';
import { ImportButton, PresetsSection } from './EncounterTools';
import { hasEntryDrag, readEntryDrag, type DraggedEntry } from '../../place';
import { BACK_NOTICE, BetweenTurns, useEndTurn } from './InitiativeStrip';
import { useNumberWords } from '../../table/numberWords';
import { BTN } from '../ui';
import { MatchSwatch, useColourMatch, type Matched } from '../colourMatch';
import { DelayControls } from './DelayParts';
import { clearSentEffects } from './EffectsPanel';

const btn = BTN;
const num = 'w-12 rounded bg-stone-800 px-1 py-0.5 text-xs';

function useSelection(): ReadonlySet<string> {
  const [s, setS] = useState(selection.get());
  useEffect(() => selection.subscribe(setS), []);
  return s;
}

/** What linking the selected tokens to a combatant would say (design/20 §3.1: a token moves and the DM is told). */
export function linkNote(enc: Encounter, target: Combatant, ids: string[], objects: Record<string, { name: string; owners?: string[] }>): string {
  const moved = ids.map((id) => ({ id, from: combatantOf(enc, id) })).filter((x) => x.from && x.from.id !== target.id);
  const notes = moved.map((x) => `${objects[x.id]?.name ?? 'A token'} moved from ${x.from!.name} to ${target.name}.`);
  // A player's token linked to a combatant moves on THAT combatant's turn, and its owner may move it then (issue 1022:
  // in the 2026-10-04 playtest the goblin was linked to the player's token, so the player moved it on the goblin's turn).
  for (const id of ids) {
    const o = objects[id];
    if (o && (o.owners ?? []).length > 0) notes.push(playerMoveNote(o.name, target.name));
  }
  return notes.join(' ');
}

/** The sections of the DM's tracker grouped by side (issue 1046), in this order; tokenless combatants are ungrouped. */
const SIDES: Array<{ side: Disposition | null; label: string }> = [
  { side: 'friend', label: 'Friends' }, { side: 'neutral', label: 'Neutral' }, { side: 'foe', label: 'Foes' }, { side: null, label: 'No token' },
];

/** A combatant's side (issue 1046): its first token still on the board's, or null with none — a tokenless combatant is ungrouped. */
export function sideOf(c: Combatant, objects: Record<string, TableObject>): Disposition | null {
  const t = c.tokens.map((id) => objects[id]).find((o) => o);
  return t ? dispositionOf(t) : null;
}

/**
 * The DM's tracker grouped by side (issue 1046, TOKEN-013): a DISPLAY grouping only. Each row keeps its index in the
 * initiative order, and the order itself — what the strip and the players see — is untouched. Every token has a side
 * from the start (qa-runs#1157: a player's a friend, any other a foe), so it groups from the start; null, a flat list,
 * only when no combatant has a token on the board.
 */
export function groupBySide(enc: Encounter, objects: Record<string, TableObject>): Array<{ label: string; rows: Array<{ c: Combatant; index: number }> }> | null {
  const sides = enc.combatants.map((c) => sideOf(c, objects));
  if (!sides.some((s) => s !== null)) return null;
  return SIDES.map(({ side, label }) => ({ label, rows: enc.combatants.map((c, index) => ({ c, index })).filter((_, i) => sides[i] === side) }))
    .filter((g) => g.rows.length > 0);
}

/**
 * What the DM has typed in a tracker box and not yet saved (qa-runs#165), by combatant and box: a row that is drawn
 * afresh — the tool moved to another bar, or anything else that remounts it — opens with the words still in it.
 * An entry lives from the first key to the save; a box that matches the table holds none.
 */
const drafts = new Map<string, string>();
/** For tests: no draft left over from another. */
export function clearTrackerDrafts(): void { drafts.clear(); clearSentEffects(); }

function useDraft(key: string, initial: string): [string, (v: string) => void, (v: string) => void] {
  const [value, setValue] = useState(() => drafts.get(key) ?? initial);
  const type = useCallback((v: string) => { drafts.set(key, v); setValue(v); }, [key]);
  const reset = useCallback((v: string) => { drafts.delete(key); setValue(v); }, [key]);
  return [value, type, reset];
}

/**
 * A combatant's pools by hand (issue 1524): what is left and the full size of each of the system's pools, saved as the
 * DM leaves a box; a size left blank takes the pool off. A size read from a linked sheet is here to change too.
 */
function PoolsEditor({ c }: { c: Combatant }) {
  const { act, setNotice, systemRules } = useTableState();
  const rules = systemRules.pools ?? [];
  const line = poolsLine(c.pools, rules);
  if (!rules.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 text-[0.6875rem]" data-testid={`pool-edit-${c.id}`}>
      {line && <span className="font-semibold text-sky-200" data-testid={`pools-${c.id}`}>{line}</span>}
      {rules.map((r) => <PoolBoxes key={`${r.key}:${c.pools?.[r.key]?.left ?? ''}/${c.pools?.[r.key]?.max ?? ''}`} c={c} poolKey={r.key} word={poolWord(r.label)} onSave={(max, left) => {
        const had = c.pools?.[r.key];
        if (max === null && !had) return;
        if (had && max === had.max && (left === null || left === had.left)) return;
        act({ kind: 'encounter.pool', id: c.id, key: r.key, max, ...(max !== null && left !== null ? { left: Math.min(left, max) } : {}) }, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'That pool could not be set.'); });
      }} />)}
    </div>
  );
}

function PoolBoxes({ c, poolKey, word, onSave }: { c: Combatant; poolKey: string; word: string; onSave: (max: number | null, left: number | null) => void }) {
  const had = c.pools?.[poolKey];
  const [left, setLeft] = useState(had ? String(had.left) : '');
  const [max, setMax] = useState(had ? String(had.max) : '');
  const read = (v: string): number | null => (v.trim() === '' || !Number.isInteger(Number(v)) ? null : Math.max(0, Math.min(POOL_LIMITS.max, Number(v))));
  const save = () => onSave(read(max), read(left));
  return (
    <label className="flex items-center gap-0.5" title={`${word}: what is left, and the full size. Leave the size blank to take the pool off.`}>
      {word}
      <input aria-label={`${word} left for ${c.name}`} type="number" min={0} max={POOL_LIMITS.max} className="w-10 rounded bg-stone-800 px-1 py-0.5 text-xs" value={left} placeholder="—" onChange={(e) => setLeft(e.target.value)} onBlur={save} onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
      /
      <input aria-label={`${word} size for ${c.name}`} type="number" min={0} max={POOL_LIMITS.max} className="w-10 rounded bg-stone-800 px-1 py-0.5 text-xs" value={max} placeholder="—" onChange={(e) => setMax(e.target.value)} onBlur={save} onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
    </label>
  );
}

/**
 * A combatant's initiative under passes (issue 1525): how many dice it throws (a rated count; blank, not known) and the
 * change to its score since the roll, each saved as the DM leaves the box; and the last throw's working.
 */
function PassBoxes({ c }: { c: Combatant }) {
  const { act, setNotice, systemRules } = useTableState();
  const rule = systemRules.passes;
  const [dice, setDice] = useState(c.initDice ? String(c.initDice) : '');
  const [mod, setMod] = useState(c.scoreModifier ? String(c.scoreModifier) : '');
  useEffect(() => { setDice(c.initDice ? String(c.initDice) : ''); setMod(c.scoreModifier ? String(c.scoreModifier) : ''); }, [c.initDice, c.scoreModifier]);
  if (!rule) return null;
  const said = (r: { ok: boolean; reason?: string }) => { if (!r.ok) setNotice(r.reason ?? 'That could not be set.'); };
  const saveDice = () => {
    const n = dice.trim() === '' ? null : Math.trunc(Number(dice));
    if (n !== null && !(Number.isFinite(n) && n >= 1 && n <= ENCOUNTER_LIMITS.initDice)) { setDice(c.initDice ? String(c.initDice) : ''); return; }
    if (n !== (c.initDice ?? null)) act({ kind: 'encounter.set', id: c.id, initDice: n }, said);
  };
  const saveMod = () => {
    const n = mod.trim() === '' ? 0 : Math.trunc(Number(mod));
    if (!Number.isFinite(n) || Math.abs(n) > ENCOUNTER_LIMITS.scoreModifier) { setMod(c.scoreModifier ? String(c.scoreModifier) : ''); return; }
    if (n !== (c.scoreModifier ?? 0)) act({ kind: 'encounter.set', id: c.id, scoreModifier: n }, said);
  };
  return (
    <div className="flex flex-wrap items-center gap-1 text-[0.6875rem]" data-testid={`passes-${c.id}`}>
      {rule.rated && (
        <label className="flex items-center gap-1" title={`How many d${rule.sides} its initiative throws: read from a linked sheet, or typed. Blank, it is not thrown.`}>
          Init dice <input aria-label={`Initiative dice of ${c.name}`} type="number" min={1} max={ENCOUNTER_LIMITS.initDice} className={num} placeholder="—" value={dice}
            onChange={(e) => setDice(e.target.value)} onBlur={saveDice} onKeyDown={(e) => { if (e.key === 'Enter') saveDice(); }} />
        </label>
      )}
      <label className="flex items-center gap-1" title="A change to the score since the roll, such as −2 for a wound. The phases not yet taken re-sort at once; a score at 0 or less loses its later phases.">
        change <input aria-label={`Score change of ${c.name}`} type="number" className={num} placeholder="0" value={mod}
          onChange={(e) => setMod(e.target.value)} onBlur={saveMod} onKeyDown={(e) => { if (e.key === 'Enter') saveMod(); }} />
      </label>
      {/* The wounds' change (issue 1526): the server's, from its token's condition monitor, added to the DM's. */}
      {c.woundModifier ? <span className="text-amber-300" data-testid={`wound-change-${c.id}`} title="From its wounds: the table sets it as the token's condition monitor changes.">{c.woundModifier < 0 ? '−' : '+'}{Math.abs(c.woundModifier)} wounded</span> : null}
      {c.initFaces && <span className="text-stone-300" data-testid={`init-line-${c.id}`}>{initiativeLine(c.initBonus, c.initFaces, rule.sides, c.scoreModifier ?? 0, c.woundModifier ?? 0)}</span>}
    </div>
  );
}

function CombatantRow({ c, index, enc, objects, selected, matched, offer, link }: { c: Combatant; index: number; enc: Encounter; objects: Record<string, TableObject>; selected: ReadonlySet<string>; matched?: Matched | null; offer?: LinkOffer | undefined; link: (o: LinkOffer) => void }) {
  const { act, setNotice, store, systemRules } = useTableState();
  const words = useNumberWords();
  const fog = store.state.fog;
  const [name, setName, resetName] = useDraft(`${c.id}:name`, c.name);
  const [bonus, setBonus, resetBonus] = useDraft(`${c.id}:bonus`, String(c.initBonus));
  const [roll, setRoll, resetRoll] = useDraft(`${c.id}:init`, c.initRoll === null ? '' : String(c.initRoll));
  // The boxes follow the table when its values change, never on arrival: a draft kept across a remount stays.
  const seen = useRef(`${c.name}\u0000${c.initBonus}\u0000${c.initRoll}`);
  useEffect(() => {
    const now = `${c.name}\u0000${c.initBonus}\u0000${c.initRoll}`;
    if (now === seen.current) return;
    seen.current = now;
    resetName(c.name); resetBonus(String(c.initBonus)); resetRoll(c.initRoll === null ? '' : String(c.initRoll));
  }, [c.name, c.initBonus, c.initRoll, resetName, resetBonus, resetRoll]);
  useInitDraft(c.id, roll); // a Start pressed before this box's save lands reads what it says (issue 1425's follow-up)
  const isActive = enc.running && enc.activeIndex === index;
  const onBoard = c.tokens.filter((id) => objects[id]);
  const tokenless = onBoard.length === 0;
  // The combatant's floor (qa-runs#1160; design/32 §5), when the scene has more than one: its first token's on the board.
  const floors = store.state.scene.floors ?? [];
  const floorName = floors.length > 1 && onBoard[0] ? floors.find((f) => f.id === floorOf(objects[onBoard[0]!]! as { floor?: string }, floors))?.name ?? null : null;
  // What a player would see (issue 851): with the fog on, a combatant whose tokens all stand in covered squares is hidden from them too.
  const hidden = !combatantSeen(c, { objects, ...(fog && 'extent' in fog ? { fog, grid: store.state.grid } : {}) }, { id: '', role: 'player' });
  const picked = [...selected].filter((id) => objects[id] && objects[id]!.kind === 'token' && !c.tokens.includes(id));
  const save = () => {
    const patch: { name?: string; initBonus?: number; initRoll?: number | null } = {};
    if (name.trim() && name.trim() !== c.name) patch.name = name.trim();
    const b = Math.trunc(Number(bonus)); if (Number.isFinite(b) && b !== c.initBonus) patch.initBonus = b;
    const r = roll.trim() === '' ? null : Math.trunc(Number(roll));
    if ((r === null || Number.isFinite(r)) && r !== c.initRoll) patch.initRoll = r;
    for (const k of ['name', 'bonus', 'init']) drafts.delete(`${c.id}:${k}`);
    if (Object.keys(patch).length) act({ kind: 'encounter.set', id: c.id, ...patch });
  };
  return (
    <li className={`space-y-1 py-2 ${isActive ? 'rounded bg-amber-950/40 px-1' : ''}`} data-testid={`combatant-${c.id}`} data-name={c.name} aria-current={isActive ? 'true' : undefined}>
      <form className="flex flex-wrap items-center gap-1" onSubmit={(e) => { e.preventDefault(); save(); }}>
        <span className="w-4 text-center text-amber-400" aria-hidden="true">{isActive ? '▶' : ''}</span>
        {floorName && <span className="rounded bg-stone-800 px-1 text-[0.6875rem] text-stone-300" data-testid="combatant-floor" title="Its floor">{floorName}</span>}
        {/* Colour-match (qa-runs#362): the swatch of the token the Properties panel is paired with. */}
        {matched && c.tokens.includes(matched.id) && <MatchSwatch colour={matched.colour} of={c.name} />}
        <input aria-label={`Name of ${c.name}`} className="w-28 rounded bg-stone-800 px-1 py-0.5 text-xs" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} onBlur={save} />
        <label className="flex items-center gap-1 text-[0.6875rem]">bonus <input aria-label={`Initiative bonus of ${c.name}`} type="number" className={num} value={bonus} onChange={(e) => setBonus(e.target.value)} onBlur={save} /></label>
        <label className="flex items-center gap-1 text-[0.6875rem]">{words.initiative} <input aria-label={`Initiative of ${c.name}`} type="number" className={num} placeholder="—" title="Type a number, or roll the game system's initiative dice plus the bonus" value={roll} onChange={(e) => setRoll(e.target.value)} onBlur={save} /></label>
        {/* No initiative rules, nothing the server throws (issue 1305): the roll is typed. */}
        {systemRules.initiative && <button type="button" className={btn} aria-label={`Roll initiative for ${c.name}`} onClick={() => act({ kind: 'encounter.roll', ids: [c.id] })}>Roll</button>}
        <button type="submit" className="sr-only">Save</button>
        <span className="ml-auto flex gap-1">
          <button type="button" className={btn} aria-label={`Move ${c.name} up`} disabled={index === 0} onClick={() => act({ kind: 'encounter.move', id: c.id, delta: -1 })}>↑</button>
          <button type="button" className={btn} aria-label={`Move ${c.name} down`} disabled={index === enc.combatants.length - 1} onClick={() => act({ kind: 'encounter.move', id: c.id, delta: 1 })}>↓</button>
          <button type="button" className={btn} aria-label={`Remove ${c.name} from the encounter`} title="Its tokens stay on the board." onClick={() => act({ kind: 'encounter.remove', ids: [c.id] })}>Remove</button>
        </span>
      </form>
      <div className="flex flex-wrap items-center gap-1 text-[0.6875rem] text-stone-400" data-testid={`tokens-${c.id}`}>
        {tokenless ? <span className="italic" data-testid="tokenless">no token on the board</span> : onBoard.map((id) => (
          <span key={id} className="flex items-center gap-1 rounded bg-stone-800 px-1.5 py-0.5 text-stone-200">
            {objects[id]!.name}{objects[id]!.hidden ? ' (hidden)' : ''}
            <button type="button" className="opacity-70 hover:opacity-100" aria-label={`Unlink ${objects[id]!.name} from ${c.name}`} onClick={() => act({ kind: 'encounter.link', id: c.id, tokens: onBoard.filter((t) => t !== id) })}>×</button>
          </span>
        ))}
        {c.tokens.length > onBoard.length && !tokenless && <span className="italic">a linked token is off the board</span>}
        {picked.length > 0 && (
          <button type="button" className={btn} aria-label={`Link ${picked.length === 1 ? objects[picked[0]!]!.name : `${picked.length} selected tokens`} to ${c.name}`}
            onClick={() => { const note = linkNote(enc, c, picked, objects); act({ kind: 'encounter.link', id: c.id, tokens: [...onBoard, ...picked] }, (r) => { if (r.ok && note) setNotice(note); }); }}>
            Link {picked.length === 1 ? objects[picked[0]!]!.name : `${picked.length} selected`}
          </button>
        )}
        {offer && objects[offer.token] && (
          <span className="flex flex-wrap items-center gap-1" data-testid="link-offer">
            <button type="button" className={btn} aria-label={`Link ${objects[offer.token]!.name} to ${c.name}`} onClick={() => link(offer)}>Link {objects[offer.token]!.name}</button>
            {offer.change && <span data-testid="link-change">{offer.change === 'hides' ? LINK_HIDES : LINK_SHOWS}</span>}
          </span>
        )}
        {tokenless && (
          <label className="flex items-center gap-1" title="With no token on the board, whether the players' strip carries this turn.">
            <input type="checkbox" aria-label={`Show ${c.name} to the players`} checked={c.shown} onChange={(e) => act({ kind: 'encounter.set', id: c.id, shown: e.target.checked })} /> shown to players
          </label>
        )}
        {hidden && <span className="text-stone-500" data-testid="hidden-mark">hidden from the players</span>}
      </div>
      <CombatantEntry c={c} objects={objects} />
      <PassBoxes c={c} />
      <PoolsEditor c={c} />
    </li>
  );
}

/** How many of the rows and tokens with a name in common pair up (qa-runs#1226), and what that does to the players' strip. */
export function offerCount(o: LinkOffers): string {
  const n = o.pairs.length;
  const rows = (k: number) => `${k} ${k === 1 ? 'row' : 'rows'}`;
  const tokens = (k: number) => `${k} ${k === 1 ? 'token' : 'tokens'}`;
  const hides = o.pairs.filter((p) => p.change === 'hides').length;
  const shows = o.pairs.filter((p) => p.change === 'shows').length;
  return [
    n === o.rows && n === o.tokens ? `Each of the ${tokens(n)} goes to its own row.` : `${rows(o.rows)} and ${tokens(o.tokens)}: ${n} ${n === 1 ? 'pairs' : 'pair'} up.`,
    hides ? `${LINK_HIDES}: ${hides}.` : '', shows ? `${LINK_SHOWS}: ${shows}.` : '',
  ].filter(Boolean).join(' ');
}

function DmTracker() {
  const { store, act, setNotice, systemRules } = useTableState();
  const mayAct = useMayAct();
  const { table, campaignId } = useShell();
  const selected = useSelection();
  const matched = useColourMatch(selected);
  const enc = (store.state.encounter ?? emptyEncounter()) as Encounter;
  const { request: endThisTurn, dialog: endTurnDialog } = useEndTurn(enc);
  const objects = store.state.objects as Record<string, TableObject>;
  const { request: startEncounter, prompt: startPrompt } = useStartEncounter(enc, objects, table.members);
  // A sort during a fight keeps the turn where it is, by design (the pointer follows whoever it pointed at);
  // say so, since the new order only shows from the next turn (issue 1021).
  const sort = () => {
    const current = enc.running ? enc.combatants[enc.activeIndex] : undefined;
    act({ kind: 'encounter.sort' }, (r) => {
      if (r.ok && current) setNotice(`Sorted. It is still ${current.name}'s turn; the new order runs from the next turn.`);
    });
  };
  const groups = groupBySide(enc, objects);
  // Roll monsters (qa-runs 405, 1211): only those with no number yet — each combatant no player owns a token of is thrown,
  // each player owning one is prompted. A number already there stands; the DM clears it to have it rolled again.
  const owned = (c: Combatant) => c.tokens.some((id) => (objects[id]?.owners ?? []).length > 0);
  const rollMonsters = () => {
    const blank = enc.combatants.filter((c) => c.initRoll === null);
    const ask = blank.filter(owned).map((c) => c.id);
    const ids = blank.filter((c) => !owned(c)).map((c) => c.id);
    if (!ids.length && !ask.length) { setNotice('Everyone has a number already. Clear one to roll it again.'); return; }
    act({ kind: 'encounter.roll', ids, ...(ask.length ? { ask } : {}) }, (r) => { if (!r.ok) setNotice(r.reason ?? 'Initiative could not be rolled.'); });
  };
  // What the rows may be offered (qa-runs#1226): only to whoever may link, and read from the same rule the server's links use.
  const mayLink = mayAct('encounter.link');
  const [markers, setMarkers] = useState<string[]>([]);
  useEffect(() => { let live = true; if (mayLink) void placeKinds(campaignId).then((k) => { if (live) setMarkers(k.markers); }); return () => { live = false; }; }, [mayLink, campaignId]);
  const fog = store.state.fog;
  const offers = mayLink ? linkOffers(enc, { objects, ...(fog && 'extent' in fog ? { fog, grid: store.state.grid } : {}) }, stripViewers(table.members), markers) : null;
  const link = (o: LinkOffer) => {
    const row = enc.combatants.find((c) => c.id === o.row);
    const note = row ? linkNote(enc, row, [o.token], objects) : '';
    act({ kind: 'encounter.link', id: o.row, tokens: [o.token] }, (r) => { if (r.ok && note) setNotice(note); });
  };
  const waiting = waitingFor(enc);
  const dmPools = (systemRules.pools ?? []).filter((r) => r.refresh === 'dm');
  const [name, setName] = useState('');
  const [bonus, setBonus] = useState('0');
  const [count, setCount] = useState('1');
  // A compendium result dropped here (issue 1045): the Place dialog, with the turn order ticked.
  const [dropped, setDropped] = useState<DraggedEntry | null>(null);
  const [priority, setPriority] = useState(enc.priority.join(', '));
  useEffect(() => setPriority(enc.priority.join(', ')), [enc.priority]);
  const add = (e: React.FormEvent) => {
    e.preventDefault();
    const n = Math.max(1, Math.min(50, Math.floor(Number(count) || 1)));
    const stem = name.trim();
    if (!stem) return;
    const b = Math.trunc(Number(bonus)) || 0;
    // A token's own name, for a token already in the order (qa-runs 1254): said, never a renamed row and an empty one.
    const held = n === 1 ? Object.values(objects).find((o) => o.kind === 'token' && o.name.trim().toLowerCase() === stem.toLowerCase() && enc.combatants.some((c) => c.tokens.includes(o.id))) : undefined;
    if (held) { setNotice(`${held.name} is already in the order, on its token.`); setName(''); return; }
    // Several at once are numbered by the shared rule (design/16 §7): the reducer names them as tokens are named.
    act({ kind: 'encounter.add', combatants: Array.from({ length: n }, () => ({ id: crypto.randomUUID(), name: stem, initBonus: b, tokens: [], shown: true })) });
    setName(''); setCount('1');
  };
  return (
    <div className="space-y-3 p-2 text-sm" data-testid="tracker"
      onDragOver={(e) => { if (hasEntryDrag(e.dataTransfer)) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; } }}
      onDrop={(e) => { const entry = readEntryDrag(e.dataTransfer); if (entry) { e.preventDefault(); setDropped(entry); } }}>
      {dropped && <PlaceDialog entry={dropped} at={null} tracker onClose={() => setDropped(null)} />}
      {endTurnDialog}
      <div className="flex flex-wrap items-center gap-1">
        <span className="text-xs text-stone-400" data-testid="tracker-round">{enc.running ? roundWords(enc, systemRules.units[0]?.label) : 'Not running'}</span>
        {!enc.running && <button type="button" className={`${btn} border-amber-600`} disabled={enc.combatants.length === 0} title="Sort by initiative and turn the order on at the highest" onClick={startEncounter}>Start Encounter</button>}
        {!enc.running && fightLeftovers(enc, objects).length > 0 && (
          <button type="button" className={btn} title="The tokens linked to this fight that are still on the board." onClick={() => boardBus.requestClearFight()}>Clear the fight's tokens…</button>
        )}
        {enc.running && (
          <>
            <button type="button" className={btn} onClick={endThisTurn}>{enc.passes ? 'End phase' : 'End turn'}</button>
            <button type="button" className={btn} title={BACK_NOTICE} onClick={() => act({ kind: 'encounter.back' }, (r) => { if (r.ok) setNotice(BACK_NOTICE); })}>Back</button>
            <button type="button" className={btn} onClick={() => act({ kind: 'encounter.end' })}>End Encounter</button>
            {/* Delay and Act now (qa-runs#1171): only where the table's system has a delayed action. */}
            <DelayControls />
          </>
        )}
        <span className="ml-auto flex gap-1">
          {/* Rolled and sorted by the system's rules only (issue 1305); without them the order is the DM's ↑ and ↓. */}
          {systemRules.initiative && <button type="button" className={btn} disabled={!enc.combatants.some((c) => c.initRoll === null)} title="Rolls for every combatant with no number yet. Numbers already there stay." onClick={() => act({ kind: 'encounter.roll', ids: enc.combatants.filter((c) => c.initRoll === null).map((c) => c.id) }, (r) => { if (!r.ok) setNotice(r.reason ?? 'Initiative could not be rolled.'); })}>Roll all</button>}
          {systemRules.initiative && <button type="button" className={btn} disabled={enc.combatants.length === 0} title="Rolls for each monster with no number yet, and asks each player with no number to roll or type theirs. Numbers already there stay." onClick={rollMonsters}>Roll monsters</button>}
          {systemRules.initiative && <button type="button" className={btn} disabled={enc.combatants.length === 0} onClick={sort}>Sort</button>}
          {/* The DM's Refresh (issue 1524): every pool the DM refreshes is full again — a new scene's Karma. */}
          {dmPools.length > 0 && (
            <button type="button" className={btn} disabled={!enc.combatants.some((c) => Object.values(c.pools ?? {}).some((p) => p.refresh === 'dm'))}
              title={`Fills every combatant's ${dmPools.map((r) => r.label).join(' and ')}.`}
              onClick={() => act({ kind: 'encounter.refresh' }, (r) => { if (!r.ok) setNotice(r.reason ?? 'Nothing was refreshed.'); })}>
              Refresh {dmPools.length === 1 ? poolWord(dmPools[0]!.label) : 'pools'}
            </button>
          )}
          <button type="button" className={btn} disabled={enc.combatants.length === 0} title="Every combatant leaves the tracker; their tokens stay." onClick={() => act({ kind: 'encounter.remove', ids: enc.combatants.map((c) => c.id) })}>Clear</button>
        </span>
      </div>
      {startPrompt}
      <BetweenTurns enc={enc} objects={objects} />
      {/* The players the DM's call still waits for (qa-runs 405): the DM may Roll for one who is away, or start anyway. */}
      {waiting.length > 0 && <p className="text-xs text-amber-200" data-testid="waiting-for" title="Each row's Roll rolls for a player who is away; Start Encounter starts without them.">Waiting for: {waiting.map((c) => c.name).join(', ')}</p>}
      {enc.running && enc.passes && <PhaseList enc={dmEncounterView(enc)} />}
      <form className="flex flex-wrap items-center gap-1" onSubmit={add}>
        <label htmlFor="combatant-name" className="text-xs text-stone-400">Add</label>
        <input id="combatant-name" className="w-28 rounded bg-stone-800 px-1 py-0.5 text-xs" placeholder="Goblin" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
        <label className="flex items-center gap-1 text-[0.6875rem]">bonus <input aria-label="Initiative bonus" type="number" className={num} value={bonus} onChange={(e) => setBonus(e.target.value)} /></label>
        <label className="flex items-center gap-1 text-[0.6875rem]">× <input aria-label="How many" type="number" min={1} max={50} className={num} value={count} onChange={(e) => setCount(e.target.value)} /></label>
        <button type="submit" className={btn}>Add combatant{Number(count) > 1 ? 's' : ''}</button>
      </form>
      {/* Outside the add form (2026-10-06): inside it, the import dialog's own form was nested in this one, and its
          Import submitted natively — the page reloaded and nothing was imported. */}
      <div className="flex justify-end"><ImportButton /></div>
      <p className="text-[0.6875rem] text-stone-500">Select tokens on the board and add them from the Properties panel or the Combat menu; or link them to a combatant below.</p>
      {offers && offers.pairs.length > 0 && (offers.rows > 1 || offers.tokens > 1) && (
        <p className="flex flex-wrap items-center gap-1 text-[0.6875rem] text-stone-400" data-testid="link-all">
          <button type="button" className={btn} onClick={() => offers.pairs.forEach(link)}>Link {offers.pairs.length} {offers.pairs.length === 1 ? 'row' : 'rows'} to {offers.pairs.length === 1 ? 'its token' : 'their tokens'}</button>
          <span>{offerCount(offers)}</span>
        </p>
      )}
      <ol className="divide-y divide-stone-800" data-testid="combatants">
        {enc.combatants.length === 0 && <li className="py-2 text-stone-500" data-testid="no-combatants">Nobody is in the fight yet.</li>}
        {/* One list, the side headings among the rows (qa-runs#165): a row that changes side, or the list turning
            grouped or flat, stays the same row — under a parent of its own it was drawn afresh, its boxes emptied
            mid-word and their focus gone. */}
        {(groups ?? [{ label: null, rows: enc.combatants.map((c, index) => ({ c, index })) }]).flatMap((g) => [
          ...(g.label ? [<li key={`side:${g.label}`} className="pt-2" data-testid={`side-${g.label}`}><p className="vtt-section-head">{g.label}</p></li>] : []),
          ...g.rows.map(({ c, index }) => <CombatantRow key={c.id} c={c} index={index} enc={enc} objects={objects} selected={selected} matched={matched} offer={offers?.pairs.find((p) => p.row === c.id)} link={link} />),
        ])}
      </ol>
      {groups && <p className="text-[0.6875rem] text-stone-500" data-testid="grouped-note">Grouped by side (players see only their own tokens' sides); the turn order is unchanged.</p>}
      <form className="flex flex-wrap items-center gap-1" onSubmit={(e) => { e.preventDefault(); act({ kind: 'encounter.priority', labels: priority.split(',').map((s) => s.trim()).filter(Boolean).slice(0, ENCOUNTER_LIMITS.priority).map((s) => s.slice(0, ENCOUNTER_LIMITS.label)) }); }}>
        <label htmlFor="effect-priority" className="text-xs text-stone-400" title="Which effects the stones show first, up to four. Without a list the space stays empty.">Stone effects, in order</label>
        <input id="effect-priority" className="w-40 rounded bg-stone-800 px-1 py-0.5 text-xs" placeholder="Bless, Prone, Haste" value={priority} onChange={(e) => setPriority(e.target.value)} />
        <button type="submit" className={btn}>Set</button>
      </form>
      <PresetsSection />
    </div>
  );
}

/**
 * A player reports their own initiative roll (issue 1050, COMBAT-009): a number for a combatant of theirs, sent as
 * an `encounter.set` with the roll alone — the one tracker change the server takes from a player. A blank or an
 * unchanged number sends nothing; the DM still corrects it, and a refusal is said as usual.
 */
function MyRoll({ id, name, roll }: { id: string; name: string; roll: number | null }) {
  // Only until a roll is in (qa-runs#1399): after that the number is the DM's to change, and the server refuses the player.
  if (roll !== null) return <span className="w-6 text-right font-bold tabular-nums" title="Your initiative is in. Ask the DM to change it." aria-label={`Your initiative roll for ${name}`}>{roll}</span>;
  return <MyRollBox id={id} name={name} roll={roll} />;
}

function MyRollBox({ id, name, roll }: { id: string; name: string; roll: number | null }) {
  const { act } = useTableState();
  const [draft, setDraft] = useState(roll === null ? '' : String(roll));
  useEffect(() => setDraft(roll === null ? '' : String(roll)), [roll]);
  const save = () => {
    const n = Math.trunc(Number(draft));
    if (draft.trim() === '' || !Number.isFinite(n)) { setDraft(roll === null ? '' : String(roll)); return; }
    if (n !== roll) act({ kind: 'encounter.set', id, initRoll: n });
  };
  return (
    <input aria-label={`Your initiative roll for ${name}`} type="number" className={num} placeholder="—" title="Type what you rolled for initiative"
      value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={save} onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
  );
}

function PlayerTracker() {
  const { store, act, systemRules } = useTableState();
  const enc = (store.state.encounter ?? emptyEncounter()) as PlayerEncounter;
  const active = enc.active ? enc.combatants.find((c) => c.id === enc.active) ?? null : null;
  const endLocked = useViewOnlyFor()('encounter.next');
  return (
    <div className="space-y-2 p-2 text-sm" data-testid="tracker">
      <div className="flex items-center gap-2 text-xs text-stone-400">
        <span data-testid="tracker-round">{enc.running ? roundWords(enc, systemRules.units[0]?.label) : 'No encounter is running.'}</span>
        {active?.mine && <button type="button" className={`${btn} border-amber-500 text-amber-300`} disabled={endLocked} title={endLocked ? VIEW_ONLY_REASON : undefined}
          onClick={() => { const b = endTurn(enc); if (b) act(b); }}>{enc.passes ? 'End my phase' : 'End my turn'}</button>}
      </div>
      {enc.running && enc.passes && <PhaseList enc={enc} />}
      {enc.running && <DelayControls />}
      <ol className="divide-y divide-stone-800" data-testid="combatants">
        {enc.combatants.length === 0 && <li className="py-2 text-stone-500" data-testid="no-combatants">{enc.running ? 'Nobody you can see is in the fight.' : 'Nobody is in the fight yet.'}</li>}
        {enc.combatants.map((c) => (
          <li key={c.id} className={`flex flex-wrap items-center gap-2 py-1.5 ${enc.active === c.id ? 'rounded bg-amber-950/40 px-1' : ''}`} data-testid={`combatant-${c.id}`} data-name={c.name} aria-current={enc.active === c.id ? 'true' : undefined}>
            {c.mine ? <MyRoll id={c.id} name={c.name ?? ''} roll={c.initRoll ?? null} /> : <span className="w-6 text-right font-bold tabular-nums">{typeof c.initRoll === 'number' ? c.initRoll : '—'}</span>}
            <span>{c.name}</span>
            {c.mine && <span className="text-[0.6875rem] text-amber-300">yours</span>}
            {c.mine && c.initFaces && systemRules.passes && <span className="text-[0.6875rem] text-stone-300" data-testid={`init-line-${c.id}`}>{initiativeLine(c.initBonus ?? 0, c.initFaces, systemRules.passes.sides, c.scoreModifier ?? 0, c.woundModifier ?? 0)}</span>}
            {c.pools && poolsLine(c.pools, systemRules.pools) && <span className="text-[0.6875rem] font-semibold text-sky-200" data-testid={`pools-${c.id}`}>{poolsLine(c.pools, systemRules.pools)}</span>}
          </li>
        ))}
      </ol>
    </div>
  );
}

export function TrackerPanel() {
  const { role } = useTableState();
  // The DM's tracker on the DM's board, with `tokens` (issues 1159, 1160): the turn order's acts are its.
  const runs = useMayAct()('encounter.next');
  return role === 'dm' && runs ? <DmTracker /> : <PlayerTracker />;
}
